import React from 'react';
import {
	Image,
	ImageBackground,
	ImageSourcePropType,
	StyleSheet,
	Text,
	TextInput,
	View,
} from 'react-native';

import {
	DuelGiftBoxPng,
	DuelPayPalPng,
	DuelRealWorldRewardPng,
	DuelRewardBannerBgPng,
	DuelVoucherPng,
} from '../../../assets/duels';
import { IconInfoSvg } from '../../../assets/tournaments/redesign';
import type { DuelRewardMethod } from '../../../services/api';
import { achievementsColors } from '../Achievements/achievementsTheme';
import { Pressable } from '../common/Touchable';
import { eventColors } from '../Event/eventTheme';
import { DuelPanel, duelTextStyles } from './DuelsUi';

type DetailRowProps = {
	icon?: ImageSourcePropType | React.ReactNode;
	label: string;
	value: React.ReactNode;
	scale: number;
};

export const DuelDetailRow = ({ icon, label, value, scale }: DetailRowProps) => (
	<View style={[styles.row, { minHeight: 34 * scale, columnGap: 8 * scale }]}>
		{icon ? (
			<View style={[styles.rowIcon, { width: 26 * scale, height: 26 * scale }]}>
				{React.isValidElement(icon) ? (
					icon
				) : (
					<Image
						source={icon as ImageSourcePropType}
						style={{ width: 24 * scale, height: 24 * scale }}
						resizeMode='contain'
					/>
				)}
			</View>
		) : null}
		<Text style={[duelTextStyles.title, styles.flex, { fontSize: 15 * scale }]}>{label}</Text>
		{typeof value === 'string' ? (
			<Text style={[duelTextStyles.cream, styles.value, { fontSize: 13 * scale }]}>{value}</Text>
		) : (
			<View style={styles.rowValue}>{value}</View>
		)}
	</View>
);

type SectionProps = { title: string; scale: number; width: number; children: React.ReactNode };

export const DuelSection = ({ title, scale, width, children }: SectionProps) => (
	<DuelPanel scale={scale} width={width} style={{ rowGap: 6 * scale, padding: 9 * scale }}>
		<Text style={[duelTextStyles.gold, { fontSize: 18 * scale }]}>{title}</Text>
		{children}
	</DuelPanel>
);

type InfoBoxProps = { text: string; scale: number; width: number };

export const DuelInfoBox = ({ text, scale, width }: InfoBoxProps) => (
	<DuelPanel scale={scale} width={width} style={{ padding: 13 * scale }}>
		<View style={[styles.infoRow, { columnGap: 8 * scale }]}>
			<IconInfoSvg width={20 * scale} height={20 * scale} />
			<Text style={[duelTextStyles.body, styles.flex, { fontSize: 12 * scale }]}>{text}</Text>
		</View>
	</DuelPanel>
);

type BannerProps = {
	seasonName: string;
	amount: string;
	tag: string;
	scale: number;
	width: number;
};

export const DuelRewardBanner = ({ seasonName, amount, tag, scale, width }: BannerProps) => {
	const height = 140 * scale;
	const radius = 12 * scale;
	return (
		<View
			style={[
				styles.banner,
				{ width, height, borderRadius: radius, borderWidth: Math.max(1, 1 * scale) },
			]}
		>
			<ImageBackground
				source={DuelRewardBannerBgPng}
				resizeMode='cover'
				style={StyleSheet.absoluteFill}
				imageStyle={{ borderRadius: radius }}
			/>
			<Image
				source={DuelRealWorldRewardPng}
				style={{ width: 124 * scale, height: 124 * scale, marginLeft: 26 * scale }}
				resizeMode='contain'
			/>
			<View style={[styles.bannerCopy, { rowGap: 4 * scale, paddingRight: 18 * scale }]}>
				<Text numberOfLines={1} style={[duelTextStyles.title, { fontSize: 18 * scale }]}>
					{seasonName}
				</Text>
				<Text
					numberOfLines={2}
					adjustsFontSizeToFit
					style={[styles.amount, { fontSize: 40 * scale, lineHeight: 46 * scale }]}
				>
					{amount}
				</Text>
				<View
					style={[
						styles.tag,
						{
							paddingHorizontal: 8 * scale,
							paddingVertical: 3 * scale,
							borderWidth: Math.max(1, 1 * scale),
						},
					]}
				>
					<Text style={[duelTextStyles.cream, { fontSize: 10 * scale }]}>{tag}</Text>
				</View>
			</View>
		</View>
	);
};

const METHOD_ART: Record<DuelRewardMethod, ImageSourcePropType> = {
	PayPal: DuelPayPalPng,
	GiftCard: DuelGiftBoxPng,
	Voucher: DuelVoucherPng,
};

type MethodOptionProps = {
	method: DuelRewardMethod;
	name: string;
	hint: string;
	selected: boolean;
	onPress: () => void;
	scale: number;
};

export const DuelMethodOption = ({
	method,
	name,
	hint,
	selected,
	onPress,
	scale,
}: MethodOptionProps) => (
	<Pressable
		accessibilityRole='radio'
		accessibilityState={{ checked: selected }}
		onPress={onPress}
		style={[
			styles.method,
			{
				minHeight: 66 * scale,
				padding: 13 * scale,
				columnGap: 12 * scale,
				borderRadius: 10 * scale,
				borderWidth: Math.max(1, 1 * scale),
			},
			selected ? styles.methodSelected : null,
		]}
	>
		<View
			style={[
				styles.methodArt,
				{ width: 40 * scale, height: 40 * scale, borderRadius: 8 * scale },
				method === 'PayPal' ? styles.methodArtPlate : null,
			]}
		>
			<Image
				source={METHOD_ART[method]}
				style={{
					width: (method === 'PayPal' ? 28 : 40) * scale,
					height: (method === 'PayPal' ? 28 : 40) * scale,
				}}
				resizeMode='contain'
			/>
		</View>
		<View style={[styles.flex, { rowGap: 2 * scale }]}>
			<Text style={[duelTextStyles.title, { fontSize: 16 * scale }]}>{name}</Text>
			<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>{hint}</Text>
		</View>
		<View
			style={[
				styles.radio,
				{ width: 20 * scale, height: 20 * scale, borderWidth: Math.max(1.5, 1.5 * scale) },
				selected ? styles.radioSelected : null,
			]}
		>
			{selected ? (
				<View style={[styles.radioDot, { width: 10 * scale, height: 10 * scale }]} />
			) : null}
		</View>
	</Pressable>
);

type EmailFieldProps = {
	label: string;
	value: string;
	onChange: (value: string) => void;
	invalid: boolean;
	error: string;
	maxLength: number;
	scale: number;
};

export const DuelEmailField = ({
	label,
	value,
	onChange,
	invalid,
	error,
	maxLength,
	scale,
}: EmailFieldProps) => (
	<View style={{ rowGap: 6 * scale }}>
		<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>{label}</Text>
		<TextInput
			style={[
				styles.input,
				{
					height: 40 * scale,
					paddingHorizontal: 12 * scale,
					borderRadius: 8 * scale,
					fontSize: 16 * scale,
				},
				invalid ? styles.inputError : null,
			]}
			value={value}
			onChangeText={next => onChange(next.slice(0, maxLength))}
			keyboardType='email-address'
			autoCapitalize='none'
			autoCorrect={false}
			autoComplete='email'
			returnKeyType='done'
			accessibilityLabel={label}
			accessibilityHint={invalid ? error : undefined}
		/>
		{invalid ? <Text style={[styles.error, { fontSize: 12 * scale }]}>{error}</Text> : null}
	</View>
);

type RewardTileProps = {
	art: ImageSourcePropType;
	title: string;
	footer: React.ReactNode;
	newLabel?: string;
	onPress?: () => void;
	width: number;
	scale: number;
};

export const DuelRewardTile = ({
	art,
	title,
	footer,
	newLabel,
	onPress,
	width,
	scale,
}: RewardTileProps) => {
	const frame = [
		styles.tile,
		{
			width,
			height: 166 * scale,
			padding: 8 * scale,
			borderRadius: 10 * scale,
			borderWidth: Math.max(1, 1 * scale),
		},
	];
	const content = (
		<>
			<Image
				source={art}
				style={{ width: 72 * scale, height: 72 * scale, marginTop: 4 * scale }}
				resizeMode='contain'
			/>
			<Text
				numberOfLines={2}
				style={[
					duelTextStyles.title,
					styles.tileTitle,
					{ fontSize: 13 * scale, lineHeight: 15 * scale },
				]}
			>
				{title}
			</Text>
			<View style={styles.tileFooter}>{footer}</View>
			{newLabel ? (
				<View
					style={[
						styles.newBadge,
						{
							top: 6 * scale,
							left: 6 * scale,
							paddingHorizontal: 6 * scale,
							paddingVertical: 2 * scale,
						},
					]}
				>
					<Text style={[styles.newLabel, { fontSize: 10 * scale }]}>{newLabel}</Text>
				</View>
			) : null}
		</>
	);
	return onPress ? (
		<Pressable
			onPress={onPress}
			accessibilityRole='button'
			accessibilityLabel={newLabel ? `${title}, ${newLabel}` : title}
			style={frame}
		>
			{content}
		</Pressable>
	) : (
		<View style={frame}>{content}</View>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	rowIcon: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	value: {
		flexShrink: 1,
		textAlign: 'right',
	},
	rowValue: {
		alignSelf: 'center',
		alignItems: 'flex-end',
	},
	infoRow: {
		flexDirection: 'row',
		alignItems: 'flex-start',
	},
	banner: {
		flexDirection: 'row',
		alignItems: 'center',
		overflow: 'hidden',
		borderColor: eventColors.countdownBorder,
	},
	bannerCopy: {
		flex: 1,
		minWidth: 0,
		alignItems: 'flex-end',
	},
	amount: {
		fontWeight: '900',
		color: eventColors.gold,
		textAlign: 'right',
	},
	tag: {
		borderRadius: 999,
		borderColor: eventColors.goldSoft,
	},
	method: {
		flexDirection: 'row',
		alignItems: 'center',
		borderColor: 'rgba(224,135,18,0.45)',
		backgroundColor: 'rgba(0,0,0,0.25)',
	},
	methodSelected: {
		borderColor: eventColors.panelBorderBright,
		backgroundColor: 'rgba(255,199,71,0.12)',
	},
	methodArt: {
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	methodArtPlate: {
		backgroundColor: eventColors.white,
	},
	radio: {
		borderRadius: 999,
		borderColor: eventColors.label,
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioSelected: {
		borderColor: eventColors.goldStrong,
	},
	radioDot: {
		borderRadius: 999,
		backgroundColor: eventColors.goldStrong,
	},
	input: {
		borderWidth: 1,
		borderColor: '#5C2416',
		backgroundColor: achievementsColors.well,
		color: achievementsColors.gold50,
		paddingVertical: 0,
	},
	inputError: {
		borderColor: '#FF8B77',
	},
	error: {
		color: '#FF8B77',
		fontWeight: '600',
	},
	tile: {
		alignItems: 'center',
		borderColor: eventColors.countdownBorder,
		backgroundColor: '#2A0804',
	},
	tileTitle: {
		textAlign: 'center',
		marginTop: 6,
	},
	tileFooter: {
		flex: 1,
		justifyContent: 'flex-end',
		alignItems: 'center',
		alignSelf: 'stretch',
	},
	newBadge: {
		position: 'absolute',
		borderRadius: 999,
		backgroundColor: eventColors.goldStrong,
	},
	newLabel: {
		color: '#3A1200',
		fontWeight: '900',
	},
});
